/**
 * The one reader of a command-line flag. F-16, one layer up from money.
 *
 * F-16 made the one-shared-module call for parsing money and figures after
 * three independent comma bugs passed three sets of green tests. The same
 * shape reappeared in the commands themselves: TA-349 found `--limit=1O`
 * labelling the whole corpus, TA-354 found `--out=-` writing a file called
 * `-` and reporting it as written, and TA-356 found a malformed `--parallel`
 * allocating zero workers and returning success having run nothing. Each was
 * fixed where it was found, and the third fix left three correct copies of
 * one decision.
 *
 * The fourth copy arrived before this module did. `scripts/verification/
 * runner.mjs` landed a day earlier with a reader of its own, stricter than
 * the other three, so `--seed 1e3` was refused where `--limit=1e3` was read
 * as a thousand. Nobody wrote the looser rule on purpose; it is what
 * `Number` does. That is the divergence F-16 predicts, found in the wild,
 * and the four are unified here at the strictest of the four readings.
 *
 * This module owns three decisions and deliberately owns nothing else:
 *
 *   1. what the command line actually said about a flag,
 *   2. what a readable whole number is, and
 *   3. what a readable date is (loop item L7).
 *
 * It renders no message. A failed read returns a reason, because the sentence
 * belongs to the command: the person reading it typed `--parallel`, not
 * `flag`, and the command is the only thing that knows what the flag is for
 * and what it was about to spend. "Keeping each CLI's own refusal wording" is
 * the brief this was built from.
 *
 * It is plain TypeScript with no imports, and every construct in it erases,
 * so `node scripts/whatever.mjs` can import it directly under Node's type
 * stripping. That matters: `scripts/` is not a pnpm workspace and is not
 * typechecked, which is why the shared module was not built when TA-356
 * asked for it. On Node 24, which is what this repository and its CI run,
 * that obstacle is gone. `scripts/cliFlags.test.mjs` holds it open by
 * spawning a real `node` with no bundler in front of it.
 */

/**
 * One occurrence of a flag on the command line.
 *
 * `spelling` is the text the person typed, so a refusal can quote them back
 * rather than a normalized form they never wrote. `raw` is the value as
 * given, unparsed and untrimmed: reading it is the second decision, below,
 * and keeping them apart is what lets a command tell "given twice" from
 * "given once and unreadable".
 */
export interface FlagOccurrence {
  readonly spelling: string;
  readonly raw: string | undefined;
}

/**
 * Every occurrence of `--name`, in both spellings, in the order they were
 * typed. Never one: a command that can only see the first cannot refuse the
 * second, which is exactly how `--limit=10 --limit=2000` came to run at 10.
 *
 * `--name=<value>` carries its own value, including an empty one.
 * `--name <value>` takes the following token, unless that token is itself a
 * flag or there is no following token, in which case the occurrence has no
 * value. A flag is never another flag's value: `--out --dry-run` is an
 * output path that was not given, not an output path called `--dry-run`.
 *
 * A negative number is a value rather than a flag, so a command can refuse
 * `--limit -3` in its own words instead of reporting `--limit` as valueless
 * and leaving `-3` to be misread as an option.
 */
export function flagOccurrences(argv: readonly string[], name: string): readonly FlagOccurrence[] {
  const bare = `--${name}`;
  const joined = `${bare}=`;
  const found: FlagOccurrence[] = [];
  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (arg === undefined) continue;
    if (arg.startsWith(joined)) {
      found.push({ spelling: arg, raw: arg.slice(joined.length) });
      continue;
    }
    // Exact, so `--limit-ms` is a different flag from `--limit` rather than a
    // longer spelling of it.
    if (arg !== bare) continue;
    const next = argv[i + 1];
    if (next === undefined || isFlag(next)) {
      found.push({ spelling: arg, raw: undefined });
      continue;
    }
    found.push({ spelling: `${arg} ${next}`, raw: next });
    i++;
  }
  return found;
}

/**
 * `argv` with every occurrence of `--name` removed, along with the value each
 * one consumed. What is left is what the next reader should see.
 *
 * A command that reads one flag and hands the rest on has to hand over
 * exactly what it did not consume, and hand-written filters are where the
 * separated spelling gets forgotten. `pnpm label:corpus` reads `--limit` and
 * passes the remainder to `resolveCorpus`, which refuses any option it does
 * not recognize (TA-162): a `--limit` left behind would be refused as a typo
 * the operator never made.
 */
export function withoutFlag(argv: readonly string[], name: string): readonly string[] {
  const bare = `--${name}`;
  const joined = `${bare}=`;
  const kept: string[] = [];
  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (arg === undefined) continue;
    if (arg.startsWith(joined)) continue;
    if (arg !== bare) {
      kept.push(arg);
      continue;
    }
    const next = argv[i + 1];
    if (next !== undefined && !isFlag(next)) i++;
  }
  return kept;
}

/**
 * A token that is an option rather than a value.
 *
 * `-3` is a number, and a lone `-` is the conventional name for standard
 * input or output. Both are values a command may want to refuse in its own
 * words, and reading either as a flag would report a missing value the person
 * did not leave out.
 */
function isFlag(token: string): boolean {
  if (token === "-") return false;
  return token.startsWith("-") && !Number.isFinite(Number(token));
}

/** Why a value could not be read. The command turns this into a sentence. */
export type UnreadableReason = "missing" | "unreadable" | "below-minimum";

export type NumberRead =
  | { readonly ok: true; readonly n: number }
  | { readonly ok: false; readonly reason: UnreadableReason };

/**
 * The one decision about what a readable whole number is.
 *
 * Section 0: a value that could not be read is a loud refusal, never a
 * default. The three failures this replaces all shared one mechanism, which
 * is that JavaScript answers a malformed count with a number. `Number("")`
 * and `Number("   ")` are 0, `Number("1O")` is NaN, `Math.max(1, NaN)` is
 * NaN and `Array.from({length: NaN})` is empty, so a typo became "no limit",
 * "zero workers" or "a random seed" without anything having to fail.
 *
 * "missing" and "unreadable" are separate because they are separate
 * mistakes: nothing was typed after the flag, versus something was typed and
 * it is not a number. "below-minimum" is separate again because the value WAS
 * read, and a command that says "not a number" about `--parallel 0` is
 * telling the person something untrue about what they typed.
 *
 * The strictness is the fourth implementation's. `Number` alone reads `0x10`
 * as 16, `1e3` as 1000 and `1e20` as an integer whose arithmetic is no longer
 * exact; `scripts/verification/runner.mjs` read `--seed` through a digits
 * pattern and `Number.isSafeInteger` instead. That divergence between four
 * copies of one decision is F-16's whole argument, and unifying at the
 * strictest reading costs nothing: no caller in this repository passes any of
 * those forms, and a refusal spends nothing while a misread count spends
 * money.
 */
const DECIMAL = /^[+-]?[0-9]+$/;

export function readWholeNumber(raw: string | undefined, min: number): NumberRead {
  if (raw === undefined) return { ok: false, reason: "missing" };
  const text = raw.trim();
  if (text === "") return { ok: false, reason: "unreadable" };
  if (!DECIMAL.test(text)) return { ok: false, reason: "unreadable" };
  const n = Number(text);
  if (!Number.isSafeInteger(n)) return { ok: false, reason: "unreadable" };
  if (n < min) return { ok: false, reason: "below-minimum" };
  // `Number("-0")` is -0, which is >= 0 and would reach a caller as a count
  // that formats as "-0". No caller has to think about that.
  return { ok: true, n: n === 0 ? 0 : n };
}

/**
 * Every argument a command that takes only on-off switches did not
 * understand, as typed and in order. Empty means the line was read in full.
 *
 * A switch read with `argv.includes` is off when it is misspelt, and nothing
 * says so: `pnpm eval --require-basline` ran as if the flag were absent, and
 * on canary.yml that makes a run whose embedding model never loaded count as
 * a real result (batch5-candidates, from TA-359). A known switch is matched
 * exactly, so a spelling with a value (`--require-baseline=false`) is not the
 * switch and comes back here, as does any word or single-dash option. One
 * bare `--` is skipped, since a package runner may pass it through; what
 * follows it is still checked. Repeating a known switch is harmless and is not
 * returned.
 *
 * Like the rest of this module it renders no message; the command words the
 * refusal.
 */
export function unrecognizedArgs(args: readonly string[], known: readonly string[]): readonly string[] {
  const out: string[] = [];
  let separatorSeen = false;
  for (const arg of args) {
    if (arg === "--" && !separatorSeen) {
      separatorSeen = true;
      continue;
    }
    if (!known.includes(arg)) out.push(arg);
  }
  return out;
}

/** A read date, or why it could not be read. The command turns this into a sentence. */
export type DateRead =
  | { readonly ok: true; readonly date: Date }
  | { readonly ok: false; readonly reason: "missing" | "unreadable" };

/**
 * The one decision about what a readable date is, for `--since` and
 * `--until`. Loop item L7 moved it here from `measureShadowCli.ts`, whose
 * private copy was the strictest of the two in packages/db (`telemetryCli.ts`
 * hands anything to `Date`), so `pnpm measure:sales` reads a date the same
 * way and there is one place to change it.
 *
 * A calendar date (`2026-10-01`, that day's UTC midnight) or a full
 * timestamp with its zone (`2026-10-01T12:30Z`, `...+02:00`). A timestamp
 * without a zone is refused, because the instant it names depends on the
 * machine the command runs on. `Date` alone also reads `10/01/2026` as
 * local time and rolls `2026-02-30` into March; both are refused here, the
 * second by checking that the day it named is a day the month has.
 */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2}))?$/;

export function readIsoDate(raw: string | undefined): DateRead {
  if (raw === undefined) return { ok: false, reason: "missing" };
  const parts = ISO_DATE.exec(raw);
  if (parts === null) return { ok: false, reason: "unreadable" };
  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const calendar = new Date(Date.UTC(year, month - 1, day));
  if (calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1 || calendar.getUTCDate() !== day) {
    return { ok: false, reason: "unreadable" };
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? { ok: false, reason: "unreadable" } : { ok: true, date };
}
